import { Check, Circle, Loader2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useHubStatus, useRecordIssue } from "@/lib/api";
import { logger } from "@/lib/logger";

import {
  AppBar,
  formatStatusDetail,
  HelpHint,
  NeedsVeluxNotice,
  PhaseDots,
  PillButton,
  StepList,
  STUCK_HELP_MS,
  StuckHelp,
  useRetryGate,
} from "./components";
import { hubUnreachableRecord, settledCaption } from "./hub-unreachable";
import { HUB_STEP_TOTAL, STEP_TITLE } from "./network-setup-screen";
import { PRODUCT_NAMES } from "./product-names";

/**
 * Phase 1 · Home Hub setup — Figma 531:173 (instructions), 519:2027
 * (connecting), 519:2093 (ready).
 *
 * The connecting stage polls the backend's hub-status endpoint, which calls
 * the household's Home Assistant with its stored ha_url + ha_token. The
 * first task completes when the hub answers; the remaining ticks are
 * presentational pacing on top of an already-confirmed connection.
 */

const CONNECT_TASKS = [
  {
    title: "Reaching your hub",
    caption: "Waiting for it to come online so we can connect remotely…",
  },
  { title: "Checking firmware", caption: null },
  { title: "Setting up your home", caption: null },
];

/** Poll cadence while waiting for the hub to come up. */
const STATUS_POLL_MS = 3000;

/** Stagger (ms) between the presentational ticks after the hub answers. */
const TASK_TICK_MS = 900;

/**
 * How long the "cannot reach…" detail may keep coming back before we treat it
 * as a real failure instead of a normal retry blip. Roughly ten seconds
 * covers a couple of cycles at `STATUS_POLL_MS`, matching the "retry once
 * around ten seconds" fix direction from VLX-78 (a beta house saw the message
 * flash mid-boot even though the hub came online right after).
 */
const RETRY_WINDOW_MS = 10_000;

/** Wiring instructions for the hub — Figma 1154:1086. */
const CONNECT_STEPS = [
  "Plug the white cable into the hub, and the other end into any free LAN port on your router or network switch.",
  "Plug the hub's power cable into your power strip.",
  "Wait for the white light on the front to turn solid. This takes around 30 seconds. The orange and green lights keep blinking, and that's normal. Only the white one matters.",
];

/**
 * When the help offer stops being gentle. Matches the "normally takes five
 * minutes" the heading promises: inside the window the button reassures,
 * past it the screen has run longer than it said and the copy should own
 * that instead.
 */
const STUCK_ESCALATE_MS = 5 * 60 * 1000;

/**
 * When the screen stops pretending to wait.
 *
 * Twice the five minutes the heading promises, measured from arriving on the
 * connecting stage. Past it a hub that has not answered is treated as a
 * failure rather than a slow boot, and the difference matters more here than
 * on a compartment: everything after this step is provisioned through the
 * hub, so there is nothing to carry on with and no skip to offer. The screen
 * says who will sort it out instead, and files the problem as urgent so the
 * report leaves while somebody can still act on it today.
 */
const HUB_UNREACHABLE_MS = 10 * 60 * 1000;

/**
 * Every stage of this module, in order — the dev screen gallery walks it, and
 * `Stage` is derived from it so the two can never disagree. Add a stage here
 * and it is in the gallery; there is no second list to remember.
 */
export const STAGES = ["instructions", "connecting", "ready"] as const;

export type Stage = (typeof STAGES)[number];

interface HubSetupScreenProps {
  busy: boolean;
  error: string;
  onComplete: () => void;
  /**
   * Stage to open on. Production always omits it (each module starts at its
   * own first stage); the dev screen gallery uses it to deep-link a stage.
   */
  initialStage?: Stage;
  /**
   * Step back to the network prerequisite that runs immediately before this
   * screen. Omitted when there is nothing behind the hub (re-opened from the
   * Home Hub grid, or the dev gallery), in which case back exits to the guide
   * as before.
   *
   * Without this, back from the first stage left the guide, and the port-count
   * answer taken one screen earlier became unreachable: `network_setup` is
   * complete by then and has no row of its own on the guide to re-enter it by.
   */
  onBackToNetwork?: () => void;
  /**
   * Force the unreachable state for the dev screen gallery, and file nothing
   * while doing it. Production always omits it: reviewing this state for real
   * means sitting on the connecting stage for ten minutes with the hub
   * unplugged, and every run would leave an urgent report on a real household.
   */
  previewUnreachable?: boolean;
}

export function HubSetupScreen({
  busy,
  error,
  onComplete,
  initialStage,
  onBackToNetwork,
  previewUnreachable = false,
}: HubSetupScreenProps) {
  const [stage, setStage] = useState<Stage>(initialStage ?? "instructions");
  const [doneTasks, setDoneTasks] = useState(0);
  // Ten minutes on the connecting stage without a hub. A clock rather than a
  // derived value, and it clears the moment the poll comes good: a hub that
  // answers at ten minutes and one second still takes the ordinary success
  // path, because the tasks below tick off the same way either way.
  const [waitedTooLong, setWaitedTooLong] = useState(false);

  // `mutate` rather than the whole result: it is stable across renders, so the
  // recording effect below is not re-run by every poll tick.
  const { mutate: recordUnreachable } = useRecordIssue();
  const recordedRef = useRef(false);

  const hubStatus = useHubStatus({
    enabled: stage !== "instructions",
    refetchInterval: stage === "connecting" ? STATUS_POLL_MS : undefined,
  });
  const reachable = hubStatus.data?.reachable === true;

  // Surface why the hub isn't answering yet: the backend's reachable=false
  // detail. Gated by useRetryGate below so a poll cycle or two of "not
  // reachable yet" during a normal boot never reaches the screen.
  const waitingDetail = formatStatusDetail(hubStatus.data?.detail);
  const retryState = useRetryGate(
    stage === "connecting" && Boolean(waitingDetail),
    RETRY_WINDOW_MS,
  );
  const visibleDetail = retryState === "failed" ? waitingDetail : null;
  const unreachable = previewUnreachable || (waitedTooLong && !reachable);

  // The clock behind the state above. Restarted rather than resumed whenever
  // the stage or the hub's answer changes, so a wait only counts from the
  // point the screen actually started waiting.
  useEffect(() => {
    if (stage !== "connecting" || reachable) {
      setWaitedTooLong(false);
      return;
    }
    const timer = setTimeout(() => setWaitedTooLong(true), HUB_UNREACHABLE_MS);
    return () => clearTimeout(timer);
  }, [stage, reachable]);

  // Files the stuck hub once, so support hears about it while the resident is
  // still standing there. `urgent` on the record (see `hubUnreachableRecord`)
  // sends the report at once instead of waiting for the hourly sweep, which
  // is the whole point: nothing after this step can run, so an hour spent
  // waiting for a sweep is an hour of a household that cannot move.
  //
  // The ref, not the effect's dependency list, is what makes it once: the
  // status poll re-renders this screen every few seconds, and each extra row
  // would be another urgent email about the same stuck hub.
  useEffect(() => {
    if (!unreachable || recordedRef.current) {
      return;
    }
    recordedRef.current = true;
    const body = hubUnreachableRecord({
      doneTasks,
      totalTasks: CONNECT_TASKS.length,
      lastDetail: waitingDetail,
    });
    if (previewUnreachable) {
      logger.debug("[onboarding] preview mode, hub issue not recorded", body);
      return;
    }
    logger.debug("[onboarding] hub unreachable, recording as urgent", body);
    recordUnreachable(body, {
      onError: (err) => logger.warn("[onboarding] could not record the hub issue", err),
    });
  }, [unreachable, doneTasks, waitingDetail, previewUnreachable, recordUnreachable]);

  // Task 0 completes only when the hub really answers; the rest tick through
  // on a short stagger, then flip to the ready stage.
  useEffect(() => {
    if (stage !== "connecting") {
      return;
    }
    if (doneTasks === 0) {
      if (reachable) {
        logger.debug("[onboarding] hub reachable", hubStatus.data);
        setDoneTasks(1);
      }
      return;
    }
    if (doneTasks >= CONNECT_TASKS.length) {
      setStage("ready");
      return;
    }
    const timer = setTimeout(() => setDoneTasks((n) => n + 1), TASK_TICK_MS);
    return () => clearTimeout(timer);
  }, [stage, doneTasks, reachable, hubStatus.data]);

  // ── Dot 4 · wire the hub up — Figma 1154:1058 ────────────────────────────
  if (stage === "instructions") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="home_assistant"
          stage={stage}
          title={STEP_TITLE}
          onBack={onBackToNetwork}
        />
        <PhaseDots total={HUB_STEP_TOTAL} activeIndex={3} />
        <div className="flex flex-1 flex-col gap-8 px-4 pt-8">
          <div className="flex flex-col gap-4">
            <h1 className="text-primary type-title">Connect your {PRODUCT_NAMES.hub}</h1>
            <p className="text-muted text-[17px] leading-6">
              It&apos;s in compartment 1, with a white ethernet cable.
            </p>
            <img
              src="/brand/onboarding/hub-connect-confirm.png"
              alt={`The ${PRODUCT_NAMES.hub} cabled to the network switch and confirmed with a green check`}
              className="w-full object-cover"
              style={{ aspectRatio: "1448/1086" }}
            />
          </div>
          <StepList steps={CONNECT_STEPS} />
          <HelpHint
            question="White light still blinking after 2 minutes?"
            answer="Check both cable ends are clicked in firmly, and that your router has internet (can your phone get online on Wi-Fi?). Still blinking? Unplug the hub's power, wait 10 seconds, plug it back in."
          />
        </div>
        <div className="sticky bottom-0 z-10 bg-white px-6 pt-4 pb-8">
          <PillButton
            label="The white light is solid"
            onClick={() => {
              setDoneTasks(0);
              setStage("connecting");
            }}
          />
        </div>
      </div>
    );
  }

  if (stage === "connecting") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
        <AppBar
          stepKey="home_assistant"
          stage={stage}
          title={STEP_TITLE}
          onBack={() => setStage("instructions")}
        />
        <PhaseDots total={HUB_STEP_TOTAL} activeIndex={4} />
        {/* pb-8 so the last block (the notice and the chat offer, once the
            wait has run out) does not sit flush against the bottom edge. */}
        <div className="flex flex-col gap-6 px-7 pt-4 pb-8">
          {/* Once the wait has run out the hero stops spinning: a spinner over
              "we could not reach your hub" says the app is still trying, and
              it is not. The red cross is the one the rest of the flow uses for
              a step that did not happen. */}
          <div className="flex items-center justify-center pt-2">
            <div className="bg-sky-subtle flex size-[140px] items-center justify-center rounded-full">
              {unreachable ? (
                <X className="text-destructive size-12" strokeWidth={2.5} aria-hidden />
              ) : (
                <Loader2 className="text-sky size-12 animate-spin" strokeWidth={2.5} />
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2 text-center">
            {/* The heading owns the outcome rather than leaving the resident to
                work it out from a checklist that stopped moving. The subtitle
                names how long it has been, because that is the promise the
                screen opened on, and points at the notice instead of making
                the same promise twice. */}
            {unreachable ? (
              <>
                <h1 className="text-primary type-headline">
                  We could not reach your {PRODUCT_NAMES.hub}
                </h1>
                <p className="text-muted text-[15px] leading-[22px]">
                  It has been ten minutes. The details are below.
                </p>
              </>
            ) : (
              <>
                <h1 className="text-primary type-headline">This normally takes five minutes</h1>
                {/* Sets up the stuck-help offer below rather than fighting it:
                    the nudge can appear well before five minutes are up
                    without reading as "this is broken", because the promise
                    already said so. See STUCK_ESCALATE_MS for the matching
                    second half. */}
                <p className="text-muted text-[15px] leading-[22px]">
                  If it takes longer, we&apos;ll figure it out together.
                </p>
                <p className="text-muted text-[15px] leading-[22px]">
                  Your hub is powering up and connecting to the internet, then we reach it remotely
                  to finish setup. You can keep this screen open. It updates on its own.
                </p>
              </>
            )}
          </div>
          <div className="flex flex-col gap-3">
            {CONNECT_TASKS.map((task, index) => {
              const isDone = index < doneTasks;
              const isActive = index === doneTasks;
              // The poll's own reason outranks the written caption once it is
              // trusted: it says why this check has not passed, which the
              // caption can only guess at.
              const caption = index === 0 && visibleDetail ? visibleDetail : task.caption;
              return (
                <div
                  key={task.title}
                  className="bg-background flex items-start gap-3 rounded-2xl p-4"
                >
                  {isDone ? (
                    <Check className="text-success size-6 shrink-0" />
                  ) : isActive && unreachable ? (
                    <X className="text-destructive size-6 shrink-0" aria-hidden />
                  ) : isActive ? (
                    <Loader2 className="text-sky size-6 shrink-0 animate-spin" />
                  ) : (
                    <Circle className="text-border size-6 shrink-0" />
                  )}
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <p className="text-primary text-[17px] leading-6">{task.title}</p>
                    {isActive && caption && (
                      <p className="text-muted type-body-sm break-words">
                        {unreachable ? settledCaption(caption) : caption}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Goes once the wait is over. It promises something the screen has
              stopped doing, directly above a notice saying nobody is waiting
              on this hub any more. */}
          {!unreachable && (
            <p className="text-muted type-body-sm text-center">
              We keep checking every few seconds while the hub boots.
            </p>
          )}
          {/* No CTA in the notice, and none under it. Every later compartment
              is provisioned through this hub, so "carry on with the rest"
              would be a button to nowhere. What the resident can do is put the
              phone down, which the paused promise says. */}
          {unreachable && <NeedsVeluxNotice paused />}
          {/* The offer of the chat survives the notice: the promise says what
              VELUX will do, and the question people are left holding is what
              happens to them in the meantime. Shown at once, because a screen
              that has already given up has nothing left to wait and see
              about. */}
          {unreachable ? (
            <StuckHelp
              stepKey="home_assistant"
              stage={stage}
              lastError={waitingDetail}
              afterMs={0}
              hasFailed
              seed="The app says my hub could not be reached and VELUX will reach out. What happens now?"
              label="Ask me what happens now"
            />
          ) : (
            <StuckHelp
              stepKey="home_assistant"
              stage={stage}
              lastError={waitingDetail}
              seed="My hub is still connecting. What is it doing right now?"
              label="Wondering what it's doing? Ask me anything"
              afterMs={STUCK_HELP_MS}
              escalateAfterMs={STUCK_ESCALATE_MS}
              escalateLabel="Past five minutes? Ask me what to check"
              escalateSeed="The hub setup has been checking for over five minutes and hasn't connected. What's it waiting on?"
            />
          )}
        </div>
      </div>
    );
  }

  const hubRows = [
    { label: "Home", value: hubStatus.data?.location_name || "Your home" },
    { label: "Software", value: hubStatus.data?.version ? `${hubStatus.data.version}` : "—" },
    { label: "Status", value: "Online" },
  ];

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
      <AppBar stepKey="home_assistant" stage={stage} title={STEP_TITLE} />
      <PhaseDots total={HUB_STEP_TOTAL} activeIndex={4} />
      <div className="flex flex-1 flex-col gap-6 px-7 pt-6">
        <div className="flex items-center justify-center">
          <div className="bg-success-subtle flex size-[100px] items-center justify-center rounded-full">
            <div className="bg-success flex size-16 items-center justify-center rounded-full">
              <Check className="size-8 text-white" strokeWidth={3} />
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2 text-center">
          <h1 className="text-primary type-headline">Your {PRODUCT_NAMES.hub} is online</h1>
          <p className="text-muted text-[17px] leading-6">
            Configured for your home, firmware up to date.
          </p>
        </div>
        <div className="bg-background flex flex-col gap-3 rounded-2xl p-5">
          <p className="text-muted type-label">Hub status</p>
          {hubRows.map((row) => (
            <div key={row.label} className="flex items-start gap-4">
              <p className="text-muted type-caption whitespace-nowrap">{row.label}</p>
              <p className="text-primary flex-1 text-right text-[15px] leading-[22px]">
                {row.value}
              </p>
            </div>
          ))}
        </div>
      </div>
      <div className="sticky bottom-0 z-10 flex flex-col gap-3 bg-white px-6 pt-4 pb-7">
        {error && (
          <p role="alert" className="text-destructive text-center text-[15px]">
            {error}
          </p>
        )}
        <PillButton label="See what's next" busy={busy} onClick={onComplete} />
      </div>
    </div>
  );
}
